import * as Sentry from '@sentry/react';

/**
 * Browser error reporting. Delivered config is environment-agnostic at build
 * time; the DSN and stage/prod `environment` arrive from the server at runtime
 * (see `/api/observability/config`). Init is idempotent and a no-op without a
 * DSN, so local dev — where the endpoint 404s — stays silent.
 */
export interface FrontendSentryConfig {
  readonly dsn: string;
  readonly environment?: string;
  readonly release?: string;
}

let initialized = false;

export function initFrontendSentry(config: FrontendSentryConfig): boolean {
  if (initialized) return true;
  if (typeof config.dsn !== 'string' || config.dsn === '') return false;
  Sentry.init({
    dsn: config.dsn,
    ...(config.environment !== undefined ? { environment: config.environment } : {}),
    ...(config.release !== undefined ? { release: config.release } : {}),
    // Errors only — no performance tracing until we opt in explicitly.
    tracesSampleRate: 0,
    integrations: [
      // Session Replay records the DOM/console/network of the failing session —
      // invaluable for "white screen" reports. Text is masked and media blocked
      // for privacy; sampled only on error (never per-session) to keep both the
      // Sentry replay quota and privacy exposure minimal.
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1,
  });
  initialized = true;
  return true;
}

export function frontendSentryEnabled(): boolean {
  return initialized;
}

export function captureFrontendException(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (!initialized) return;
  Sentry.captureException(
    error,
    context === undefined ? undefined : { extra: context },
  );
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
